/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useRef, useCallback } from 'react'
import { helpUrlsService } from './api/helpUrls'
import { getCurrentLang } from './auth'
import { useLocation } from 'react-router'

export interface HelpPageSection {
  [stateName: string]: string
}

export interface HelpPageConfig {
  baseUrl: string
  section: HelpPageSection
}

export interface HelpPageHookReturn {
  baseUrl: string
  stateName: string
  section: HelpPageSection

  loadAndInit: () => Promise<void>
  setStateName: (stateName: string) => void
  hasStateHelpUrl: (stateName: string) => boolean
  getHelpUrl: (stateName?: string) => string
  getLangCode: () => string
  getVideosUrl: () => string
  getKnowledgeSpaceUrl: () => string
  getSupportCenterUrl: () => string

  isLoading: boolean
  error: Error | null
}

const SUPPORT_CENTER_URL = 'https://support.mdsol.com/hc'
const VIDEOS_URL = 'https://learn.medidata.com/en-US/bundle/rcm/page/rcm_video_library.html'
const KNOWLEDGESPACE_URL = 'https://learn.medidata.com/en-US/bundle/rcm/page/medidata_regulated_content_management.html'
const HELP_URL_LINK_PREFIX = 'page'

export function useHelpPage(): HelpPageHookReturn {
  const [baseUrl, setBaseUrl] = useState<string>('')
  const [currentStateName, setCurrentStateName] = useState<string>('')
  const [section, setSection] = useState<HelpPageSection>({})
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const initializedRef = useRef<boolean>(false)
  const location = useLocation()

  const loadAndInit = useCallback(async (): Promise<void> => {
    if (initializedRef.current) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = await helpUrlsService.getAllHelpUrls()
      const processedBaseUrl = String(data?.baseUrl).replace('_LNK_PREFIX_', HELP_URL_LINK_PREFIX)

      setSection(data.section && typeof data.section === 'object' ? (data.section as HelpPageSection) : {})
      setBaseUrl(processedBaseUrl)

      //Note: it contains more logic i:e The hook automatically updates stateName when routes change which need to be done where this function will be used via useLocation hook
      initializedRef.current = true
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load help URLs')
      setError(error)
      console.error('Failed to initialize help page service:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const setStateName = useCallback(
    (newStateName: string): void => {
      setCurrentStateName(newStateName)
    },
    [setCurrentStateName]
  )

  const hasStateHelpUrl = useCallback(
    (targetStateName: string): boolean => {
      return Boolean(section?.[targetStateName])
    },
    [section]
  )

  const getHelpUrl = useCallback(
    (targetStateName?: string): string => {
      if (!baseUrl || !section) {
        return ''
      }

      let url = baseUrl

      if (targetStateName) {
        if (section[targetStateName]) {
          url += section[targetStateName]
        } else {
          return ''
        }
      } else if (currentStateName && hasStateHelpUrl(currentStateName)) {
        url += section[currentStateName]
      } else if (location?.pathname && hasStateHelpUrl(location.pathname)) {
        //Note: need verification
        url += section[location.pathname]
      }

      return url + '?lang=' + getLangCode()
    },
    [baseUrl, section, currentStateName, hasStateHelpUrl]
  )

  const getLangCode = useCallback((): string => {
    const currentLang = getCurrentLang()
    return currentLang.indexOf('-') > -1 ? currentLang.slice(0, currentLang.indexOf('-')) : currentLang
  }, [])

  const getVideosUrl = useCallback((): string => {
    return VIDEOS_URL + '?lang=' + getLangCode()
  }, [getLangCode])

  const getKnowledgeSpaceUrl = useCallback((): string => {
    return KNOWLEDGESPACE_URL + '?lang=' + getLangCode()
  }, [getLangCode])

  const getSupportCenterUrl = useCallback((): string => {
    return SUPPORT_CENTER_URL + '/' + getCurrentLang().toLowerCase()
  }, [])

  return {
    baseUrl,
    stateName: currentStateName,
    section,

    loadAndInit,
    setStateName,
    hasStateHelpUrl,
    getHelpUrl,
    getLangCode,
    getVideosUrl,
    getKnowledgeSpaceUrl,
    getSupportCenterUrl,

    isLoading,
    error
  }
}
